<template>
  <ts-card>
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('payFixedAllowance.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper>
      <ts-loading-banner :loading="loadingPreData">
        <form>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payFixedAllowance.employeeId') }}</label>
                <div>
                  <ts-select
                    v-model="model.employee_id"
                    :options="
                      employees.map(e => ({
                        text: e.employee_name_en,
                        value: e.employee_id
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('employee_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('employee_id')">{{ errors.first('employee_id') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payFixedAllowance.allowanceId') }}</label>
                <div>
                  <ts-select
                    v-model="model.allowance_id"
                    :options="
                      allowances.map(e => ({
                        text: e.allowance_item,
                        value: e.allowance_id
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('allowance_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('allowance_id')">
                    {{ errors.first('allowance_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payFixedAllowance.allowance') }}</label>
                <div>
                  <ts-input
                    type="number"
                    v-model.number="model.allowance"
                    :class="{ 'is-invalid': errors.has('allowance') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('allowance')">{{ errors.first('allowance') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payFixedAllowance.effectveDate') }}</label>
                <div>
                  <ts-date-picker-input v-model="model.effectve_date" />
                  <div class="invalid-feedback" v-if="errors.has('effectve_date')">
                    {{ errors.first('effectve_date') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label">{{ $t('payFixedAllowance.expiryDate') }}</label>
                <div>
                  <ts-date-picker-input v-model="model.expiry_date" />
                  <div class="invalid-feedback" v-if="errors.has('expiry_date')">{{ errors.first('expiry_date') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label">{{ $t('payFixedAllowance.description') }}</label>
                <div>
                  <ts-textarea v-model="model.description" rows="3" />
                </div>
              </div>
            </div>
          </div>
          <div class="row ts-mt-6">
            <div class="col-sm-12 col-lg-12 col-md-12 ts-text-right">
              <ts-button @click.prevent="$emit('close')" :disabled="saving">{{ $t('button.cancel') }}</ts-button>
              <ts-button
                outline
                color="primary"
                class="ml-2"
                :waiting="savingAndNew"
                @click.prevent="saveAndNew"
                v-if="editData === undefined"
                >{{ $t('button.createAndNew') }}</ts-button
              >
              <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">
                {{ editData ? $t('button.update') : $t('button.create') }}
              </ts-button>
            </div>
          </div>
        </form>
      </ts-loading-banner>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep } from 'lodash'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'pay-fixed-allowance-form',
  props: ['editData'],
  data() {
    const model = {
      employee_id: this.editData ? this.editData.employee_id : '',
      allowance_id: this.editData ? this.editData.allowance_id : '',
      allowance: this.editData ? this.editData.allowance : '',
      effectve_date: this.editData ? this.editData.effectve_date : '',
      expiry_date: this.editData ? this.editData.expiry_date : '',
      description: this.editData ? this.editData.description : ''
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      loadingPreData: false
    }
  },
  computed: {
    ...mapState('hr/payFixedAllowance', ['employees', 'allowances']),
    dayRange: vm => (start, end) => {
      return range(start, end)
    }
  },
  created() {
    this.loadingPreData = true

    Promise.all([this.fetchEmployees(), this.fetchAllowances()]).then(responses => {
      this.loadingPreData = false
    })
  },
  methods: {
    ...mapActions('hr/payFixedAllowance', ['fetchEmployees', 'fetchAllowances']),
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('hr/payFixedAllowance/update', {
          id: this.editData.record_id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('hr/payFixedAllowance/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('hr/payFixedAllowance/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          if (error.errors) this.errors = new Errors(error.errors)
          else this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
